import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Pagination from '@mui/material/Pagination';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import BusinessIcon from '@mui/icons-material/Business';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormLabel from '@mui/material/FormLabel';
import {
    getBadgeAccessInfo,
    saveAudience,
    listCodes,
    listUsers,
    generateCodes,
    addUsers,
    revokeCodes,
    revokeUsers,
    issueManual,
    BadgeAccessInfo,
    AccessCode,
    AccessUser,
} from '../api/badge-access';

const PAGE_SIZE = 25;

function statusColor(status: string | null): 'default' | 'warning' | 'success' | 'error' {
    switch (status) {
        case 'Earned': return 'success';
        case 'Not Started': return 'warning';
        case 'Revoked': return 'error';
        default: return 'default';
    }
}

// ---------------------------------------------------------------------------
// Audience Section
// ---------------------------------------------------------------------------
function AudienceSection({ badgeInfo, onSaved }: { badgeInfo: BadgeAccessInfo; onSaved: (updated: Partial<BadgeAccessInfo>) => void }) {
    const [audience, setAudience] = useState<'public' | 'private'>(badgeInfo.isPrivate ? 'private' : 'public');
    const [limitMode, setLimitMode] = useState<'unlimited' | 'limited'>(badgeInfo.isUnlimited ? 'unlimited' : 'limited');
    const [limitCount, setLimitCount] = useState(badgeInfo.limitCount > 0 ? badgeInfo.limitCount : 25);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState('');

    const handleSave = async () => {
        setSaving(true);
        const isPrivate = audience === 'private';
        const isUnlimited = limitMode === 'unlimited';
        const res = await saveAudience(badgeInfo.badgeID, isPrivate, isUnlimited, isUnlimited ? undefined : limitCount);
        if (res.success) {
            setSnackbar('Audience settings saved.');
            onSaved({ isPrivate, isUnlimited, limitCount: isUnlimited ? 0 : limitCount });
        } else {
            setSnackbar(res.message ?? 'Failed to save settings.');
        }
        setSaving(false);
    };

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Audience</Typography>
            <Divider sx={{ mb: 2 }} />

            <Box display="flex" gap={4} flexWrap="wrap">
                {/* Audience */}
                <Box>
                    <FormLabel sx={{ display: 'block', mb: 1, fontSize: 13, fontWeight: 600 }}>Visibility</FormLabel>
                    <RadioGroup value={audience} onChange={e => setAudience(e.target.value as 'public' | 'private')}>
                        <FormControlLabel value="public" control={<Radio size="small" />} label="Public" />
                        <FormControlLabel value="private" control={<Radio size="small" />} label="Private" />
                    </RadioGroup>
                </Box>

                {/* Issuing limits */}
                <Box>
                    <FormLabel sx={{ display: 'block', mb: 1, fontSize: 13, fontWeight: 600 }}>Issuing Limits</FormLabel>
                    <RadioGroup value={limitMode} onChange={e => setLimitMode(e.target.value as 'unlimited' | 'limited')}>
                        <FormControlLabel value="unlimited" control={<Radio size="small" />} label="Unlimited" />
                        <FormControlLabel
                            value="limited"
                            control={<Radio size="small" />}
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <span>Max awards:</span>
                                    <TextField
                                        type="number"
                                        size="small"
                                        value={limitCount}
                                        onChange={e => setLimitCount(Math.max(1, Number(e.target.value)))}
                                        onClick={() => setLimitMode('limited')}
                                        disabled={limitMode === 'unlimited'}
                                        inputProps={{ min: 1 }}
                                        sx={{ width: 80 }}
                                    />
                                </Box>
                            }
                        />
                    </RadioGroup>
                </Box>
            </Box>

            <Button variant="contained" sx={{ mt: 2 }} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Settings'}
            </Button>

            <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar('')} message={snackbar} />
        </Paper>
    );
}

// ---------------------------------------------------------------------------
// QR Section
// ---------------------------------------------------------------------------
function QRSection({ badgeInfo }: { badgeInfo: BadgeAccessInfo }) {
    const [snackbar, setSnackbar] = useState('');
    const claimUrl = badgeInfo.badgeShortCode
        ? `${window.location.origin}/badges/claim/${badgeInfo.badgeShortCode}`
        : null;
    const qrSrc = claimUrl
        ? `/api/qr?url=${encodeURIComponent(claimUrl)}&format=png`
        : null;

    const copyLink = () => {
        if (!claimUrl) return;
        navigator.clipboard.writeText(claimUrl).then(() => setSnackbar('Link copied!'));
    };

    const downloadQR = () => {
        if (!qrSrc) return;
        const a = document.createElement('a');
        a.href = qrSrc;
        a.download = `${badgeInfo.badgeName}-qr.png`;
        a.click();
    };

    if (!claimUrl) {
        return (
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>Self-Claim QR Code</Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">No short code assigned to this badge.</Typography>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Self-Claim QR Code</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" gap={3} flexWrap="wrap" alignItems="flex-start">
                <Box component="img" src={qrSrc ?? ''} alt="QR code" sx={{ width: 160, height: 160, border: '1px solid', borderColor: 'divider', borderRadius: 1 }} />
                <Box flex={1} minWidth={200}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Share this link or print the QR code — anyone with it can claim this badge.
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        value={claimUrl}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title="Copy link">
                                        <IconButton size="small" onClick={copyLink}>
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 1.5 }}
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={downloadQR}
                    >
                        Download QR Code
                    </Button>
                </Box>
            </Box>
            <Snackbar open={!!snackbar} autoHideDuration={2500} onClose={() => setSnackbar('')} message={snackbar} />
        </Paper>
    );
}

// ---------------------------------------------------------------------------
// Unique Codes Section
// ---------------------------------------------------------------------------
function CodesSection({ badgeID }: { badgeID: string }) {
    const [codes, setCodes] = useState<AccessCode[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [generateCount, setGenerateCount] = useState(5);
    const [generating, setGenerating] = useState(false);
    const [revoking, setRevoking] = useState(false);
    const [snackbar, setSnackbar] = useState('');

    const load = useCallback((p: number) => {
        setLoading(true);
        listCodes(badgeID, p, PAGE_SIZE)
            .then((res) => {
                if (res.success) {
                    setCodes(res.data ?? []);
                    setTotal(res.total ?? 0);
                }
            })
            .finally(() => setLoading(false));
    }, [badgeID]);

    useEffect(() => { load(page); }, [page, load]);

    const toggleSelect = (code: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    };

    const toggleAll = () => {
        const available = codes.filter(c => !c.Email);
        setSelected(prev => prev.size === available.length ? new Set() : new Set(available.map(c => c.UniqueCode)));
    };

    const handleGenerate = async () => {
        setGenerating(true);
        const res = await generateCodes(badgeID, generateCount);
        if (res.success) {
            setSnackbar(`${res.data?.codes.length ?? generateCount} code(s) generated.`);
            setPage(1);
            load(1);
        } else {
            setSnackbar(res.message ?? 'Failed to generate codes.');
        }
        setGenerating(false);
    };

    const handleRevoke = async () => {
        if (selected.size === 0) return;
        setRevoking(true);
        const res = await revokeCodes(badgeID, Array.from(selected));
        if (res.success) {
            setSnackbar(`${selected.size} code(s) voided.`);
            setSelected(new Set());
            load(page);
        } else {
            setSnackbar(res.message ?? 'Failed to void codes.');
        }
        setRevoking(false);
    };

    const availableForVoid = codes.filter(c => !c.Email && selected.has(c.UniqueCode));
    const totalPages = Math.ceil(total / PAGE_SIZE);

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Unique Codes</Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Generate controls */}
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <TextField
                    type="number"
                    size="small"
                    label="Count"
                    value={generateCount}
                    onChange={e => setGenerateCount(Math.max(1, Math.min(1000, Number(e.target.value))))}
                    sx={{ width: 90 }}
                    inputProps={{ min: 1, max: 1000 }}
                />
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleGenerate}
                    disabled={generating}
                >
                    {generating ? 'Generating…' : 'Generate Codes'}
                </Button>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
            ) : codes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No unique codes yet.</Typography>
            ) : (
                <>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            size="small"
                                            indeterminate={selected.size > 0 && selected.size < codes.filter(c => !c.Email).length}
                                            checked={selected.size > 0 && selected.size === codes.filter(c => !c.Email).length}
                                            onChange={toggleAll}
                                        />
                                    </TableCell>
                                    <TableCell>Code</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Redeemed by</TableCell>
                                    <TableCell>Redeemed date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {codes.map(code => (
                                    <TableRow key={code.UniqueCode} selected={selected.has(code.UniqueCode)}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                checked={selected.has(code.UniqueCode)}
                                                onChange={() => toggleSelect(code.UniqueCode)}
                                                disabled={!!code.Email}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{code.UniqueCode}</TableCell>
                                        <TableCell>
                                            <Chip label={code.Status ?? 'Unclaimed'} size="small" color={statusColor(code.Status)} />
                                        </TableCell>
                                        <TableCell>{code.Email ?? '—'}</TableCell>
                                        <TableCell>
                                            {code.RedeemedDate
                                                ? new Date(code.RedeemedDate).toLocaleDateString()
                                                : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>

                    {totalPages > 1 && (
                        <Box display="flex" justifyContent="center" mt={2}>
                            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} size="small" />
                        </Box>
                    )}

                    <Box display="flex" justifyContent="flex-end" mt={2}>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            disabled={availableForVoid.length === 0 || revoking}
                            onClick={handleRevoke}
                        >
                            {revoking ? 'Voiding…' : `Void Selected (${availableForVoid.length})`}
                        </Button>
                    </Box>
                </>
            )}

            <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar('')} message={snackbar} />
        </Paper>
    );
}

// ---------------------------------------------------------------------------
// Named Users Section
// ---------------------------------------------------------------------------
function UsersSection({ badgeID }: { badgeID: string }) {
    const [users, setUsers] = useState<AccessUser[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [emailInput, setEmailInput] = useState('');
    const [adding, setAdding] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [snackbar, setSnackbar] = useState('');

    const load = useCallback((p: number) => {
        setLoading(true);
        listUsers(badgeID, p, PAGE_SIZE)
            .then((res) => {
                if (res.success) {
                    setUsers(res.data ?? []);
                    setTotal(res.total ?? 0);
                }
            })
            .finally(() => setLoading(false));
    }, [badgeID]);

    useEffect(() => { load(page); }, [page, load]);

    const toggleSelect = (uid: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(uid)) next.delete(uid); else next.add(uid);
            return next;
        });
    };

    const handleAdd = async () => {
        const emails = emailInput.split(',').map(e => e.trim()).filter(Boolean);
        if (emails.length === 0) return;
        setAdding(true);
        const res = await addUsers(badgeID, emails);
        if (res.success) {
            setSnackbar(`${res.data?.added ?? 0} user(s) added.`);
            setEmailInput('');
            setPage(1);
            load(1);
        } else {
            setSnackbar(res.message ?? 'Failed to add users.');
        }
        setAdding(false);
    };

    const handleRemove = async () => {
        if (selected.size === 0) return;
        setRemoving(true);
        const res = await revokeUsers(badgeID, Array.from(selected));
        if (res.success) {
            setSnackbar(`${selected.size} user(s) removed.`);
            setSelected(new Set());
            load(page);
        } else {
            setSnackbar(res.message ?? 'Failed to remove users.');
        }
        setRemoving(false);
    };

    const removableSelected = Array.from(selected).filter(uid => {
        const u = users.find(u => u.UserID === uid);
        return u?.Status === 'Not Started';
    });

    const totalPages = Math.ceil(total / PAGE_SIZE);

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Named Users</Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Add users */}
            <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                <TextField
                    size="small"
                    placeholder="email@example.com, another@example.com"
                    value={emailInput}
                    onChange={e => setEmailInput(e.target.value)}
                    sx={{ flex: 1, minWidth: 260 }}
                />
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleAdd}
                    disabled={adding || !emailInput.trim()}
                >
                    {adding ? 'Adding…' : 'Add Users'}
                </Button>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
            ) : users.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No named users yet.</Typography>
            ) : (
                <>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            size="small"
                                            indeterminate={selected.size > 0 && selected.size < users.filter(u => u.Status === 'Not Started').length}
                                            checked={selected.size > 0 && selected.size === users.filter(u => u.Status === 'Not Started').length}
                                            onChange={() => {
                                                const removable = users.filter(u => u.Status === 'Not Started');
                                                setSelected(prev => prev.size === removable.length ? new Set() : new Set(removable.map(u => u.UserID)));
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Last modified</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map(user => (
                                    <TableRow key={user.UserID} selected={selected.has(user.UserID)}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                checked={selected.has(user.UserID)}
                                                onChange={() => toggleSelect(user.UserID)}
                                                disabled={user.Status !== 'Not Started'}
                                            />
                                        </TableCell>
                                        <TableCell>{user.Email}</TableCell>
                                        <TableCell>
                                            <Chip label={user.Status} size="small" color={statusColor(user.Status)} />
                                        </TableCell>
                                        <TableCell>
                                            {user.LastModifiedDate
                                                ? new Date(user.LastModifiedDate).toLocaleDateString()
                                                : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>

                    {totalPages > 1 && (
                        <Box display="flex" justifyContent="center" mt={2}>
                            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} size="small" />
                        </Box>
                    )}

                    <Box display="flex" justifyContent="flex-end" mt={2}>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            disabled={removableSelected.length === 0 || removing}
                            onClick={handleRemove}
                        >
                            {removing ? 'Removing…' : `Remove Selected (${removableSelected.length})`}
                        </Button>
                    </Box>
                </>
            )}

            <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar('')} message={snackbar} />
        </Paper>
    );
}

// ---------------------------------------------------------------------------
// Manual Issue Section
// ---------------------------------------------------------------------------
function ManualIssueSection({ badgeID }: { badgeID: string }) {
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [deliverAt, setDeliverAt] = useState('');
    const [noNotification, setNoNotification] = useState(false);
    const [issuing, setIssuing] = useState(false);
    const [snackbar, setSnackbar] = useState('');

    const handleIssue = async () => {
        if (!email.trim()) return;
        setIssuing(true);
        const res = await issueManual(
            badgeID,
            email.trim(),
            firstName.trim() || undefined,
            lastName.trim() || undefined,
            deliverAt || undefined,
            noNotification || undefined,
        );
        if (res.success) {
            setSnackbar(res.data?.message ?? 'Badge issued successfully.');
            setEmail('');
            setFirstName('');
            setLastName('');
            setDeliverAt('');
            setNoNotification(false);
        } else {
            setSnackbar(res.message ?? 'Failed to issue badge.');
        }
        setIssuing(false);
    };

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Manual Issue</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" mb={2}>
                Issue this badge directly to a user by email address.
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
                <TextField
                    size="small"
                    label="Email *"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    sx={{ flex: 2, minWidth: 200 }}
                />
                <TextField
                    size="small"
                    label="First name"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    sx={{ flex: 1, minWidth: 120 }}
                />
                <TextField
                    size="small"
                    label="Last name"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    sx={{ flex: 1, minWidth: 120 }}
                />
            </Box>
            <Box display="flex" gap={2} flexWrap="wrap" mt={2} alignItems="center">
                <TextField
                    size="small"
                    label="Deliver on (optional)"
                    type="date"
                    value={deliverAt}
                    onChange={e => setDeliverAt(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 220 }}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={noNotification}
                            onChange={e => setNoNotification(e.target.checked)}
                        />
                    }
                    label="Issue without notifying the recipient"
                />
                <Button
                    variant="contained"
                    onClick={handleIssue}
                    disabled={issuing || !email.trim()}
                >
                    {issuing ? 'Issuing…' : 'Issue Badge'}
                </Button>
            </Box>
            <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar('')} message={snackbar} />
        </Paper>
    );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function BadgeAccessPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [badgeInfo, setBadgeInfo] = useState<BadgeAccessInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;
        getBadgeAccessInfo(id)
            .then(res => {
                if (res.success && res.data) {
                    setBadgeInfo(res.data);
                } else {
                    setError(res.message ?? 'Badge not found.');
                }
            })
            .catch(() => setError('Failed to load badge.'))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !badgeInfo || !id) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/badges')} sx={{ mb: 2 }}>
                    Back to Manage Badges
                </Button>
                <Alert severity="error">{error ?? 'Badge not found.'}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            {/* Back */}
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/badges')} sx={{ mb: 3 }}>
                Back to Manage Badges
            </Button>

            {/* Badge header */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" alignItems="center" gap={2}>
                    {badgeInfo.badgeImage ? (
                        <Box
                            component="img"
                            src={badgeInfo.badgeImage}
                            alt={badgeInfo.badgeName}
                            sx={{ width: 72, height: 72, objectFit: 'contain', borderRadius: 1 }}
                        />
                    ) : (
                        <WorkspacePremiumIcon sx={{ fontSize: 56, color: 'primary.main' }} />
                    )}
                    <Box>
                        <Typography variant="h5" fontWeight={700}>{badgeInfo.badgeName}</Typography>
                        {badgeInfo.issuerName && (
                            <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">{badgeInfo.issuerName}</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Paper>

            <AudienceSection
                badgeInfo={badgeInfo}
                onSaved={(updated) => setBadgeInfo(prev => prev ? { ...prev, ...updated } : prev)}
            />
            <QRSection badgeInfo={badgeInfo} />
            <CodesSection badgeID={id} />
            <UsersSection badgeID={id} />
            <ManualIssueSection badgeID={id} />
        </Container>
    );
}
